// ./src/types/filters.ts
// Universal filter types for entity pages (refuels, expenses, checkpoints, travels, reports)

// ============================================
// Entity Types
// ============================================

export type EntityType =
  | "refuels"
  | "expenses"
  | "checkpoints"
  | "travels"
  | "reports"
  | "upcoming-services"
  | "glovebox"
  | "revenues"
  | "expense-schedules"
  | "vehicle-financing"
  | "tire-sets"
  | "vehicle-tasks"
  | "parking-sessions"
  | "logbook"
  | "tags"
  | "users"
  | "invitations"
  | "cars"
  | "saved-places";

// ============================================
// Universal Entity Filters
// ============================================

/**
 * Universal filter state used across all entity pages.
 * All filters are persisted in URL and localStorage.
 */
export interface EntityFilters {
  /** Selected vehicle ID */
  carId: string | null;

  /** Date range start (ISO date string YYYY-MM-DD) */
  dateFrom: string | null;

  /** Date range end (ISO date string YYYY-MM-DD) */
  dateTo: string | null;

  /** Selected tag IDs */
  tagIds: string[];

  /** Selected year (for yearly reports) */
  year: number | null;

  /** Selected expense type (numeric: 1=Refuel, 2=Expense, 3=Checkpoint, 4=Travel, 5=Revenue) */
  expenseType: number | null;
}

// ============================================
// Filter Schema for Serialization
// ============================================

export type FilterFieldType = "string" | "string[]" | "date" | "number";

export interface FilterFieldSchema {
  /** Type of the filter field for serialization */
  type: FilterFieldType;

  /** URL parameter name */
  urlParam: string;

  /** Default value when not set */
  defaultValue: unknown;
}

export type FilterSchema = Record<keyof EntityFilters, FilterFieldSchema>;

// ============================================
// Default Values
// ============================================

export const DEFAULT_ENTITY_FILTERS: EntityFilters = {
  carId: null,
  dateFrom: null,
  dateTo: null,
  tagIds: [],
  year: null,
  expenseType: null,
};

export const ENTITY_FILTER_SCHEMA: FilterSchema = {
  carId: {
    type: "string",
    urlParam: "carId",
    defaultValue: null,
  },
  dateFrom: {
    type: "date",
    urlParam: "dateFrom",
    defaultValue: null,
  },
  dateTo: {
    type: "date",
    urlParam: "dateTo",
    defaultValue: null,
  },
  tagIds: {
    type: "string[]",
    urlParam: "tagIds",
    defaultValue: [],
  },
  year: {
    type: "number",
    urlParam: "year",
    defaultValue: null,
  },
  expenseType: {
    type: "number",
    urlParam: "expenseType",
    defaultValue: null,
  },
};

// ============================================
// Navigation Mode Types
// ============================================

export type EntityNavigationMode = "list" | "add" | "edit" | "view" | "waypoints";